import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import {
  Forward10Icon,
  FullscreenIcon,
  PauseIcon,
  PlayIcon,
  Replay10Icon,
  SpeedIcon,
  SubtitlesIcon,
} from '../icons.js';
import { vfmt } from '../../lib/video-format.js';

/**
 * HTML5 video player with the OneView (Galaxy) custom-control overlay.
 *
 * Native `<video controls={false}>` for behaviour (range requests, audio,
 * fullscreen) + our overlay for the polish:
 *   - Centre play/pause button when paused.
 *   - Bottom controls: play / replay-10 / forward-10 / time / subtitles
 *     toggle / speed picker / fullscreen.
 *   - Scrubber with one tick per chapter (so the user can see where the
 *     boundaries fall before clicking).
 *   - Current chapter chip top-left.
 *
 * The container exposes a ref handle (`PlayerHandle`) so the parent page
 * can seek the player from a chapter click, transcript click, or `?t=`
 * URL param without re-mounting.
 */

export interface PlayerChapter {
  index: number;
  title: string;
  startSeconds: number;
  endSeconds: number;
}

export interface PlayerHandle {
  seek: (t: number) => void;
  play: () => void;
  pause: () => void;
  /** Read-only — last reported playhead position. */
  currentTime: () => number;
}

interface PlayerProps {
  videoId: string;
  durationSeconds: number;
  chapters?: PlayerChapter[];
  initialT?: number;
  /** Fires on every timeupdate (~4×/s). Caller debounces for progress writes. */
  onTimeUpdate?: (t: number) => void;
  /** Fires once the video reports a real duration (used to lock in totalDuration). */
  onDuration?: (t: number) => void;
}

export const Player = forwardRef<PlayerHandle, PlayerProps>(function Player(
  { videoId, durationSeconds, chapters, initialT, onTimeUpdate, onDuration },
  ref,
) {
  const vidRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(initialT ?? 0);
  const [duration, setDuration] = useState(durationSeconds || 0);
  const [speed, setSpeed] = useState(1);

  useImperativeHandle(
    ref,
    () => ({
      seek: (s: number) => {
        if (vidRef.current) vidRef.current.currentTime = s;
        setT(s);
      },
      play: () => vidRef.current?.play(),
      pause: () => vidRef.current?.pause(),
      currentTime: () => t,
    }),
    [t],
  );

  // Apply initialT once the video element is ready. Intentionally only
  // re-runs when the videoId changes — the ref-driven seek() is how
  // callers re-seek mid-session, not by changing initialT.
  useEffect(() => {
    if (initialT !== undefined && vidRef.current) {
      vidRef.current.currentTime = initialT;
      setT(initialT);
    }
  }, [videoId, initialT]);

  useEffect(() => {
    if (vidRef.current) vidRef.current.playbackRate = speed;
  }, [speed]);

  const currentChapter = chapters
    ? ([...chapters].reverse().find((c) => c.startSeconds <= t) ?? chapters[0])
    : undefined;
  const chapterIdx = chapters && currentChapter ? chapters.indexOf(currentChapter) : -1;
  const pct = duration > 0 ? Math.min(1, t / duration) : 0;

  const onScrub = (e: React.MouseEvent<HTMLDivElement>): void => {
    const track = trackRef.current;
    if (!track || !duration) return;
    const r = track.getBoundingClientRect();
    const x = Math.max(0, Math.min(r.width, e.clientX - r.left));
    const next = Math.round((x / r.width) * duration);
    if (vidRef.current) vidRef.current.currentTime = next;
    setT(next);
  };

  const togglePlay = (): void => {
    const v = vidRef.current;
    if (!v) return;
    if (v.paused) void v.play();
    else v.pause();
  };

  const skip = (delta: number): void => {
    const v = vidRef.current;
    if (!v) return;
    const next = Math.max(0, Math.min(duration, v.currentTime + delta));
    v.currentTime = next;
    setT(next);
  };

  const enterFullscreen = (): void => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen();
    else void el.requestFullscreen();
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#0E0C14',
        aspectRatio: '16 / 9',
        width: '100%',
      }}
    >
      <video
        ref={vidRef}
        src={`/api/v1/videos/${videoId}/stream`}
        // No crossOrigin — backend /stream is same-origin (cookie-auth) in prod
        // and proxied through vite in dev. Matches the admin player pattern in
        // pages/Videos.tsx; adding `use-credentials` triggers a CORS preflight
        // the backend doesn't satisfy and the video silently fails to load.
        playsInline
        controls={false}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          if (v.duration && Number.isFinite(v.duration)) {
            setDuration(v.duration);
            onDuration?.(v.duration);
          }
        }}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          setT(v.currentTime);
          onTimeUpdate?.(v.currentTime);
        }}
        onEnded={() => setPlaying(false)}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />

      {/* Centre play button when paused */}
      {!playing && (
        <button
          onClick={togglePlay}
          aria-label="Play"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 88,
            height: 88,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.94)',
            color: 'var(--ov-primary, #172E57)',
            border: 0,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 28px rgba(0,0,0,0.30)',
          }}
        >
          <PlayIcon className="w-12 h-12" />
        </button>
      )}

      {/* Now-playing chapter chip */}
      {currentChapter && (
        <div
          style={{
            position: 'absolute',
            top: 14,
            left: 14,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px 6px 8px',
            background: 'rgba(14,12,20,0.55)',
            backdropFilter: 'blur(8px)',
            color: '#fff',
            borderRadius: 999,
            font: '500 12px/16px var(--ov-font-ui, system-ui)',
            border: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.20)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              font: '700 10px/1 var(--ov-font-ui, system-ui)',
            }}
          >
            {chapterIdx + 1}
          </span>
          <span>{currentChapter.title}</span>
        </div>
      )}

      {/* Bottom controls */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '14px 16px 12px',
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.78) 100%)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {/* Scrubber */}
        <div
          ref={trackRef}
          onClick={onScrub}
          style={{
            position: 'relative',
            height: 22,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {chapters?.map((c) => (
            <span
              key={c.index}
              style={{
                position: 'absolute',
                left: `${(c.startSeconds / Math.max(1, duration)) * 100}%`,
                width: 2,
                height: 8,
                background: 'rgba(255,255,255,0.45)',
                transform: 'translate(-50%, 0)',
              }}
            />
          ))}
          <span
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: 4,
              background: 'rgba(255,255,255,0.18)',
              borderRadius: 4,
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: 0,
              height: 4,
              width: `${pct * 100}%`,
              background: '#fff',
              borderRadius: 4,
            }}
          />
          <span
            style={{
              position: 'absolute',
              left: `calc(${pct * 100}% - 7px)`,
              width: 14,
              height: 14,
              background: '#fff',
              borderRadius: '50%',
              boxShadow: '0 1px 6px rgba(0,0,0,0.4)',
            }}
          />
        </div>

        {/* Controls row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, color: '#fff' }}>
          <PlayerBtn onClick={togglePlay} title={playing ? 'Pause' : 'Play'}>
            {playing ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
          </PlayerBtn>
          <PlayerBtn onClick={() => skip(-10)} title="Back 10s">
            <Replay10Icon className="w-5 h-5" />
          </PlayerBtn>
          <PlayerBtn onClick={() => skip(10)} title="Forward 10s">
            <Forward10Icon className="w-5 h-5" />
          </PlayerBtn>
          <span
            style={{
              font: '600 12px/16px var(--ov-font-ui, system-ui)',
              fontVariantNumeric: 'tabular-nums',
              opacity: 0.92,
            }}
          >
            {vfmt(t)} <span style={{ opacity: 0.6 }}>/ {vfmt(duration)}</span>
          </span>
          <div style={{ flex: 1 }} />
          <PlayerBtn onClick={() => {}} title="Subtitles (auto)">
            <SubtitlesIcon className="w-5 h-5" />
          </PlayerBtn>
          <PlayerBtn
            onClick={() => setSpeed((s) => (s >= 2 ? 0.5 : s + 0.25))}
            title={`Speed (${speed}×)`}
          >
            <SpeedIcon className="w-5 h-5" />
          </PlayerBtn>
          <PlayerBtn onClick={enterFullscreen} title="Fullscreen">
            <FullscreenIcon className="w-5 h-5" />
          </PlayerBtn>
        </div>
      </div>
    </div>
  );
});

function PlayerBtn({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        width: 32,
        height: 32,
        borderRadius: 6,
        border: 0,
        background: 'transparent',
        color: '#fff',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.14)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {children}
    </button>
  );
}
